import { Download, Info } from 'lucide-react';
import { useScorecard, type ComplianceBand, type SectionScore } from '../../lib/api/queries';
import { BAND_COLORS, bandLabel } from './scoreCharts/bands';
import { ResultsScoreChart } from './scoreCharts/ScoreCharts';

// Re-exported for the report views that already import it from here.
export { BAND_COLORS };

/**
 * Mirrors the spreadsheet's "Org Assessment Results" tab. Big overall card
 * up top with %, total, and the named compliance band; section table below
 * with one row per section showing its %, band, and a progress bar.
 */
export function ScorecardView({ tenantId, auditId }: { tenantId: string; auditId: string }) {
  const { data, isLoading, error } = useScorecard(tenantId, auditId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading scorecard…</p>;
  if (error)     return <p className="text-sm text-danger-500">Couldn't load scorecard.</p>;
  if (!data)     return null;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <DownloadReportButton auditId={auditId} />
      </div>

      <OverallCard
        totalScore={data.overall.totalScore}
        potentialScore={data.overall.potentialScore}
        percentage={data.overall.percentage}
        band={data.overall.band}
      />

      <ResultsScoreChart sections={data.sections} />

      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <header className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Section scores</h3>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <HeaderCell align="left">Section</HeaderCell>
              <HeaderCell align="right" width="w-24"
                tooltip="Sum of scored item values ÷ potential. Each scored item counts up to 5. N/A items are excluded from both numerator and potential; unanswered items are excluded from both as well." >
                Score
              </HeaderCell>
              <HeaderCell align="right" width="w-16"
                tooltip="Compliance percentage = section score ÷ potential × 100. Unanswered and N/A items are excluded from both sides of the calculation.">
                %
              </HeaderCell>
              <HeaderCell align="left" width="w-48"
                tooltip="Visual representation of the compliance percentage. Full bar = 100%, empty = 0% or unscored.">
                Progress
              </HeaderCell>
              <HeaderCell align="left" width="w-28"
                tooltip="Compliance band derived from %: 0% = N/A, 1–29% = Very Low, 29–64% = Low, 64–84% = Medium, 84–94% = High, 94–100% = Very High.">
                Band
              </HeaderCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.sections.map(s => (
              <SectionRow key={s.sectionId} section={s} />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/**
 * Plain anchor link to the PDF export endpoint. The browser handles the
 * download lifecycle (Save dialog, progress, error) and the auth cookie
 * travels with the request automatically since it's same-origin. No JS
 * state machine needed — the server picks the filename via
 * Content-Disposition.
 */
function DownloadReportButton({ auditId }: { auditId: string }) {
  return (
    <a
      href={`/api/audit/audits/${auditId}/report/pdf`}
      download
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                 text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                 transition-colors"
    >
      <Download className="w-4 h-4" aria-hidden />
      Download PDF
    </a>
  );
}

/**
 * Table header cell with an optional info icon + native-tooltip explanation
 * of how the column's value is computed. The native tooltip is keyboard-
 * accessible (focus the icon) and works without extra deps.
 */
function HeaderCell({
  align, width, tooltip, children,
}: {
  align: 'left' | 'right';
  width?: string;
  tooltip?: string;
  children: React.ReactNode;
}) {
  const alignCls = align === 'right' ? 'text-right' : 'text-left';
  return (
    <th className={`${alignCls} px-4 py-2 font-medium ${width ?? ''}`}>
      <span className="inline-flex items-center gap-1.5">
        {children}
        {tooltip && (
          <span
            tabIndex={0}
            role="img"
            aria-label={tooltip}
            title={tooltip}
            className="text-gray-400 hover:text-gray-600 focus:text-gray-600 cursor-help outline-none"
          >
            <Info className="w-3.5 h-3.5" aria-hidden />
          </span>
        )}
      </span>
    </th>
  );
}

function OverallCard({
  totalScore, potentialScore, percentage, band,
}: {
  totalScore: number; potentialScore: number; percentage: number; band: ComplianceBand;
}) {
  return (
    <section className="rounded-lg border p-6" style={bandBackground(band)}>
      <p className="text-xs uppercase tracking-wide font-semibold text-gray-700">
        Overall compliance
      </p>
      <div className="mt-2 flex items-end gap-4 flex-wrap">
        <p className="text-5xl font-semibold text-gray-900 tabular-nums">
          {Math.round(percentage * 100)}%
        </p>
        <BandPill band={band} large />
      </div>
      <p className="mt-3 text-sm text-gray-700">
        Scored <span className="font-medium">{totalScore}</span> out of{' '}
        <span className="font-medium">{potentialScore}</span> possible points
        (N/A items excluded).
      </p>
    </section>
  );
}

function SectionRow({ section }: { section: SectionScore }) {
  const widthPct = Math.max(0, Math.min(1, section.percentage)) * 100;
  return (
    <tr>
      <td className="px-4 py-2">
        <span className="text-gray-500 mr-2">{section.code}</span>
        {section.name}
      </td>
      <td className="text-right px-4 py-2 text-gray-600 tabular-nums">
        {section.totalScore}/{section.potentialScore}
      </td>
      <td className="text-right px-4 py-2 font-medium tabular-nums">
        {Math.round(section.percentage * 100)}%
      </td>
      <td className="px-4 py-2">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full transition-all"
            style={{ width: `${widthPct}%`, backgroundColor: BAND_COLORS[section.band] }}
          />
        </div>
      </td>
      <td className="px-4 py-2">
        <BandPill band={section.band} />
      </td>
    </tr>
  );
}

export function BandPill({ band, large = false }: { band: ComplianceBand; large?: boolean }) {
  const label = bandLabel(band);
  const size  = large ? 'text-sm px-3 py-1' : 'text-[11px] px-2 py-0.5';
  return (
    <span
      className={`inline-block font-semibold rounded-full border ${size}`}
      style={bandPillStyle(band)}
    >
      {label}
    </span>
  );
}


function bandBackground(band: ComplianceBand): React.CSSProperties {
  const c = BAND_COLORS[band];
  return {
    backgroundColor: band === 'NotApplicable' ? '#f9fafb' : `${c}14`, // ~8% alpha
    borderColor:     band === 'NotApplicable' ? '#e5e7eb' : `${c}55`, // ~33% alpha
  };
}

function bandPillStyle(band: ComplianceBand): React.CSSProperties {
  const c = BAND_COLORS[band];
  if (band === 'NotApplicable') {
    return { backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#d1d5db' };
  }
  return {
    backgroundColor: `${c}1F`,  // ~12% alpha fill
    color:           c,
    borderColor:     `${c}55`,
  };
}
